import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime, num, pct } from '../lib/format.js';
import { EmptyState, ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';
import '../screens-2a.css';

interface Visit {
  id: number;
  startedAt: string;
  site: string;
  landingPath: string | null;
  engagedSeconds: number;
  maxScrollPct: number;
  keyNames: string[];
  active: boolean;
  pages: string[];
  fromMessage: {
    code: string | null;
    sentAt: string | null;
    attribution: 'exact' | 'probable' | null;
  } | null;
}
interface PanelData {
  summary: {
    visits: number;
    visitsFromEmail: number;
    activeVisits: number;
    totalEngagedSeconds: number;
    maxScrollPct: number;
    keyEvents: number;
    lastActiveAt: string | null;
  };
  visits: Visit[];
}

const duration = (s: number): string =>
  s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;

/** Panel „Chování na webu“ v kartě zákazníka: návštěvy po e-mailu, stránky, aktivní čas. */
export function CustomerWebPanel({ contactId }: { contactId: string }): JSX.Element {
  const query = useQuery<PanelData>({
    queryKey: ['customer-web', contactId],
    queryFn: () => api.get(`/api/customers/${contactId}/web`),
  });
  const d = query.data;

  return (
    <Panel
      title="Chování na webu"
      subtitle="Posledních 50 návštěv. Vidíme jen návštěvy po souhlasu s měřením."
    >
      {query.isLoading ? (
        <LoadingState what="chování na webu" />
      ) : query.isError || !d ? (
        <ErrorState
          title="Chování na webu se nepodařilo načíst"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : d.visits.length === 0 ? (
        <EmptyState title="Zatím žádná návštěva webu">
          Zatím žádná návštěva webu po souhlasu s měřením.
        </EmptyState>
      ) : (
        <>
          <dl className="kv" data-testid="web-panel-summary">
            <dt>Naposledy aktivní</dt>
            <dd>{dateTime(d.summary.lastActiveAt)}</dd>
            <dt>Návštěv</dt>
            <dd>
              {num(d.summary.visits)}, z toho po e-mailu {num(d.summary.visitsFromEmail)}, aktivních{' '}
              {num(d.summary.activeVisits)}
            </dd>
            <dt>Aktivní čas celkem</dt>
            <dd>{duration(d.summary.totalEngagedSeconds)}</dd>
            <dt>Nejhlubší scroll</dt>
            <dd>{pct(d.summary.maxScrollPct)}</dd>
            <dt>Klíčové akce</dt>
            <dd>{num(d.summary.keyEvents)}</dd>
          </dl>
          <div className="scroll">
            <table data-testid="web-panel-visits">
              <thead>
                <tr>
                  <th>Kdy</th>
                  <th>Web</th>
                  <th>Ze zprávy</th>
                  <th>Stránky</th>
                  <th className="n">Aktivní čas</th>
                  <th className="n">Scroll</th>
                  <th>Klíčové akce</th>
                </tr>
              </thead>
              <tbody>
                {d.visits.map((v) => (
                  <tr key={v.id}>
                    <td>{dateTime(v.startedAt)}</td>
                    <td>{v.site}</td>
                    <td>
                      {v.fromMessage ? (
                        <>
                          {v.fromMessage.code ?? 'zpráva'}{' '}
                          <small>({dateTime(v.fromMessage.sentAt)})</small>
                          {v.fromMessage.attribution === 'probable' ? (
                            <>
                              {' '}
                              <StatusBadge tone="warn">pravděpodobně</StatusBadge>
                            </>
                          ) : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{v.pages.length > 0 ? v.pages.join(', ') : (v.landingPath ?? '—')}</td>
                    <td className="n">
                      {v.active ? (
                        <StatusBadge tone="ok">{duration(v.engagedSeconds)}</StatusBadge>
                      ) : (
                        duration(v.engagedSeconds)
                      )}
                    </td>
                    <td className="n">{pct(v.maxScrollPct)}</td>
                    <td>{v.keyNames.length > 0 ? v.keyNames.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Panel>
  );
}
